import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';

const VendorLiveBidding = ({ onNavigate, userInfo }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vendorData, setVendorData] = useState({
    vendor_name: "Loading...",
    vendor_id: "...",
    contact_person: "",
    email: "",
    phone: "",
    company_type: "",
    vendor_status: "active"
  });

  const [dashboardStats, setDashboardStats] = useState({
    totalBids: 0,
    activeBids: 0,
    wonBids: 0,
    lostBids: 0,
    totalTenders: 0
  });

  const [tenders, setTenders] = useState([]);
  const [activeTenders, setActiveTenders] = useState([]);
  const [recentBids, setRecentBids] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedTender, setSelectedTender] = useState(null);
  const [showBidModal, setShowBidModal] = useState(false);

  // Bid Form States
  const [bidData, setBidData] = useState({
    quotedAmount: '',
    taxPercentage: '18',
    taxAmount: '',
    totalAmount: '',
    paymentTerms: '',
    deliveryPeriod: '',
    warrantyPeriod: '',
    remarks: ''
  });

  const [quotationFile, setQuotationFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
      if (window.innerWidth > 1024) {
        setIsSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Reset hover states when sidebar closes
  useEffect(() => {
    if (!isSidebarOpen) {
      setHoveredItem(null);
    }
  }, [isSidebarOpen]);

  // Calculate tax and total automatically
  useEffect(() => {
    if (bidData.quotedAmount && bidData.taxPercentage) {
      const quoted = parseFloat(bidData.quotedAmount) || 0;
      const taxRate = parseFloat(bidData.taxPercentage) || 0;
      const tax = (quoted * taxRate) / 100;
      const total = quoted + tax;

      setBidData(prev => ({
        ...prev,
        taxAmount: tax.toFixed(2),
        totalAmount: total.toFixed(2)
      }));
    }
  }, [bidData.quotedAmount, bidData.taxPercentage]);

  // Fetch Active Tenders
  useEffect(() => {
    const fetchTenders = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const vendorId = userInfo?.id || userInfo?.userId || 'VEN2024001';

        if (!token) {
          setError('Authentication required. Please login again.');
          setLoading(false);
          return;
        }

        const apiUrl = `${API_LINK}/api/vendor/tenders/active?vendorId=${vendorId}`;

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleLogout();
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success && result.data) {
          setTenders(result.data.tenders || []);
          setVendorData({
            vendor_name: userInfo?.name || "Vendor",
            vendor_id: vendorId
          });
        }

      } catch (error) {
        console.error('Error fetching tenders:', error);
        setError(`Failed to load tenders: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchTenders, 500);
    return () => clearTimeout(timer);
  }, [userInfo]);

  // Handle logout
  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  // Handle navigation
  const handleNavigation = (page) => {
    if (onNavigate) {
      switch (page) {
        case 'Dashboard':
          onNavigate('vendor-dashboard');
          break;
        case 'Live Bidding':
          onNavigate('live-bidding');
          break;
        case 'My Bids':
          onNavigate('my-bids');
          break;
        case 'Profile':
          onNavigate('vendor-profile');
          break;
        case 'Logout':
          handleLogout();
          break;
        default:
          console.log('Unknown navigation:', page);
      }
    }
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  // Navigation items
  const navigationItems = [
    { name: 'Dashboard', icon: '📊', badge: null },
    { name: 'Live Bidding', icon: '📦', badge: tenders.length },
    { name: 'My Bids', icon: '📄', badge: null },
  ];

  // Open bid modal
  const handleOpenBidModal = (tender) => {
    setSelectedTender(tender);
    setShowBidModal(true);
    setUploadSuccess(false);

    // Reset form if already submitted
    if (tender.vendor_has_bid > 0) {
      setBidData({
        quotedAmount: tender.vendor_quoted_amount || '',
        taxPercentage: '18',
        taxAmount: '',
        totalAmount: '',
        paymentTerms: '',
        deliveryPeriod: '',
        warrantyPeriod: '',
        remarks: ''
      });
    } else {
      setBidData({
        quotedAmount: '',
        taxPercentage: '18',
        taxAmount: '',
        totalAmount: '',
        paymentTerms: '',
        deliveryPeriod: '',
        warrantyPeriod: '',
        remarks: ''
      });
    }
    setQuotationFile(null);
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Please upload only PDF files');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        alert('File size must be less than 10MB');
        return;
      }
      setQuotationFile(file);
    }
  };

  // Submit bid
  const handleSubmitBid = async (e) => {
    e.preventDefault();

    if (!quotationFile) {
      alert('Please upload your quotation PDF');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const vendorId = userInfo?.id || userInfo?.userId || 'VEN2024001';

      const formData = new FormData();
      formData.append('quotation', quotationFile);
      formData.append('vendorId', vendorId);
      formData.append('tenderId', selectedTender.tender_id);
      formData.append('quotedAmount', bidData.quotedAmount);
      formData.append('taxPercentage', bidData.taxPercentage);
      formData.append('taxAmount', bidData.taxAmount);
      formData.append('totalAmount', bidData.totalAmount);
      formData.append('paymentTerms', bidData.paymentTerms);
      formData.append('deliveryPeriod', bidData.deliveryPeriod);
      formData.append('warrantyPeriod', bidData.warrantyPeriod);
      formData.append('remarks', bidData.remarks);

      const apiUrl = `${API_LINK}/api/vendor/bid/submit`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setUploadSuccess(true);
        alert('Bid submitted successfully!');

        // Refresh tenders list
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        throw new Error(result.message || 'Failed to submit bid');
      }

    } catch (error) {
      console.error('Error submitting bid:', error);
      alert(`Failed to submit bid: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Download quotation
  const handleDownloadQuotation = async (bidId) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const apiUrl = `${API_LINK}/api/vendor/quotation/download/${bidId}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download quotation');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quotation-${bidId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('Error downloading quotation:', error);
      alert('Failed to download quotation');
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get days remaining
  const getDaysRemaining = (deadline) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Filter tenders
  const filteredTenders = tenders.filter(tender => {
    const matchesSearch = tender.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tender.tender_number?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = filterStatus === 'all' ||
      (filterStatus === 'submitted' && tender.vendor_has_bid > 0) ||
      (filterStatus === 'not-submitted' && tender.vendor_has_bid === 0);

    return matchesSearch && matchesFilter;
  });

  // Get user initials
  const getUserInitials = (name) => {
    if (!name) return 'VE';
    const names = name.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const colors = {
      'submitted': { bg: '#e3f2fd', color: '#1976d2' },
      'under_review': { bg: '#fff8e1', color: '#f57c00' },
      'won': { bg: '#e8f5e9', color: '#2e7d32' },
      'lost': { bg: '#ffebee', color: '#c62828' },
      'active': { bg: '#e1f5fe', color: '#0277bd' },
      'pending': { bg: '#fce4ec', color: '#c2185b' }
    };
    return colors[status] || { bg: '#f5f5f5', color: '#666' };
  };

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading tenders...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div style={{
      margin: 0,
      padding: 0,
      boxSizing: 'border-box',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      backgroundColor: '#f4f7fa',
      color: '#333',
      lineHeight: 1.6,
      minHeight: '100vh',
      width: '100%',
      overflowX: 'hidden'
    }}>
      {/* Global CSS Reset */}
      <style>
        {`
          *, *::before, *::after {
            box-sizing: border-box;
          }

          html, body {
            margin: 0;
            padding: 0;
            width: 100%;
            overflow-x: hidden;
          }
        `}
      </style>

      {/* Navigation Bar */}
      <nav style={{
        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
        color: '#ffffff',
        padding: isMobile ? '0.75rem 1rem' : '1.5rem 4rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'fixed',
        top: 0,
        right: 0,
        left: isMobile ? 0 : '280px',
        width: isMobile ? '100%' : 'calc(100% - 280px)',
        zIndex: 1000,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        minHeight: isMobile ? '60px' : '140px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '0.75rem' : '2rem'
        }}>
          <img
            src="/logo.png"
            alt="Swami Vivekananda University Logo"
            style={{
              width: isMobile ? '40px' : '120px',
              height: isMobile ? '40px' : '120px',
              objectFit: 'contain'
            }}
          />

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem'
          }}>
            <h1 style={{
              fontSize: isMobile ? '1rem' : '1.8rem',
              fontWeight: 'bold',
              margin: 0,
              whiteSpace: 'nowrap'
            }}>
              Swami Vivekananda University
            </h1>
            <p style={{
              fontSize: '1rem',
              margin: 0,
              opacity: 0.9,
              borderLeft: '2px solid rgba(255, 255, 255, 0.5)',
              paddingLeft: '1.5rem',
              whiteSpace: 'nowrap',
              display: isMobile ? 'none' : 'block'
            }}>
              Live Bidding Portal
            </p>
          </div>
        </div>

        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '8px',
            padding: '0.75rem',
            cursor: 'pointer',
            fontSize: '1.5rem',
            color: '#ffffff',
            display: isMobile ? 'block' : 'none',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
          onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
        >
          {isSidebarOpen ? '✕' : '☰'}
        </button>
      </nav>

      {/* Sidebar */}
      <aside style={{
        position: 'fixed',
        top: 0,
        left: isMobile ? (isSidebarOpen ? 0 : '-280px') : 0,
        width: '280px',
        height: '100%',
        background: '#ffffff',
        boxShadow: '2px 0 10px rgba(0,0,0,0.1)',
        transition: 'left 0.3s ease',
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto'
      }}>
        {/* Profile Section */}
        <div style={{
          background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
          color: '#ffffff',
          padding: '2.5rem 1.5rem',
          textAlign: 'center',
          borderBottom: '3px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            fontSize: '2.5rem',
            fontWeight: 'bold',
            border: '3px solid rgba(255, 255, 255, 0.4)'
          }}>
            {getUserInitials(vendorData.vendor_name)}
          </div>
          <h3 style={{
            margin: '0 0 0.5rem 0',
            fontSize: '1.2rem',
            fontWeight: 'bold'
          }}>
            {vendorData.vendor_name}
          </h3>
          <p style={{
            margin: '0 0 0.25rem 0',
            fontSize: '0.9rem',
            opacity: 0.9
          }}>
            Vendor ID: {vendorData.vendor_id}
          </p>
          <div style={{
            display: 'inline-block',
            padding: '0.35rem 1rem',
            background: 'rgba(255, 255, 255, 0.25)',
            borderRadius: '20px',
            fontSize: '0.85rem',
            marginTop: '0.5rem'
          }}>
            ● Active
          </div>
        </div>

        {/* Navigation Menu */}
        <nav style={{
          flex: 1,
          padding: '1.5rem 0'
        }}>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0
          }}>
            {navigationItems.map((item, index) => (
              <li key={index} style={{ margin: '0.5rem 0' }}>
                <button
                  onClick={() => handleNavigation(item.name)}
                  onMouseEnter={() => setHoveredItem(index)}
                  onMouseLeave={() => setHoveredItem(null)}
                  style={{
                    width: '100%',
                    padding: '1rem 1.5rem',
                    background: hoveredItem === index ? 'rgba(35, 133, 72, 0.1)' : 'transparent',
                    border: 'none',
                    borderLeft: hoveredItem === index ? '4px solid #238548' : '4px solid transparent',
                    color: hoveredItem === index ? '#238548' : '#666',
                    fontSize: '1rem',
                    fontWeight: hoveredItem === index ? 'bold' : 'normal',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    textAlign: 'left'
                  }}
                >
                  <span style={{ fontSize: '1.5rem' }}>{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.name}</span>
                  {item.badge > 0 && (
                    <span style={{
                      background: '#238548',
                      color: '#ffffff',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {item.badge}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout Button */}
        <div style={{ padding: '1.5rem' }}>
          <button
            onClick={() => handleNavigation('Logout')}
            style={{
              width: '100%',
              padding: '1rem',
              background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <span>🚪</span>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isMobile && isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}

      {/* Main Content */}
      <main style={{
        marginLeft: isMobile ? 0 : '280px',
        marginTop: isMobile ? '60px' : '140px',
        padding: isMobile ? '1.5rem' : '2.5rem',
        minHeight: 'calc(100vh - 140px)',
        transition: 'margin-left 0.3s ease'
      }}>
        {/* Header */}
        <section style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{
            margin: '0 0 0.5rem 0',
            fontSize: isMobile ? '1.5rem' : '2rem',
            color: '#238548'
          }}>
            Live Bidding Portal 📦
          </h2>
          <p style={{
            margin: 0,
            color: '#666',
            fontSize: '1rem'
          }}>
            Browse and bid on active tenders. Submit your best offers before the deadline.
          </p>
        </section>

        {error && (
          <div style={{
            background: '#ffebee',
            border: '1px solid #f44336',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <span style={{ fontSize: '1.5rem' }}>⚠️</span>
            <p style={{ margin: 0, color: '#c62828' }}>{error}</p>
          </div>
        )}

        {/* Search and Filter */}
        <section style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '1rem',
            alignItems: isMobile ? 'stretch' : 'center'
          }}>
            <div style={{ flex: 1 }}>
              <input
                type="text"
                placeholder="Search tenders by title or number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#238548'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{
                  padding: '0.75rem 1rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Tenders</option>
                <option value="not-submitted">Not Submitted</option>
                <option value="submitted">Submitted</option>
              </select>
            </div>
          </div>
        </section>

        {/* Tenders List */}
        <section style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '1.3rem',
              color: '#333'
            }}>
              Available Tenders ({filteredTenders.length})
            </h3>
          </div>

          {filteredTenders.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '4rem 2rem',
              color: '#999'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📦</div>
              <h4 style={{ margin: '0 0 1rem 0', color: '#666' }}>No tenders found</h4>
              <p style={{ margin: 0 }}>
                {searchTerm || filterStatus !== 'all'
                  ? 'Try adjusting your search or filter criteria.'
                  : 'There are currently no active tenders available for bidding.'}
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '1.5rem'
            }}>
              {filteredTenders.map((tender) => (
                <div key={tender.tender_id} style={{
                  background: '#f8f9fa',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  cursor: 'pointer'
                }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                  onClick={() => handleOpenBidModal(tender)}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{
                        margin: '0 0 0.5rem 0',
                        fontSize: '1.1rem',
                        color: '#238548',
                        fontWeight: 'bold'
                      }}>
                        {tender.title}
                      </h4>
                      <p style={{
                        margin: '0 0 0.5rem 0',
                        fontSize: '0.9rem',
                        color: '#666'
                      }}>
                        Tender #{tender.tender_number}
                      </p>
                    </div>
                    <div style={{
                      padding: '0.25rem 0.75rem',
                      background: tender.vendor_has_bid > 0 ? '#e8f5e9' : '#e3f2fd',
                      color: tender.vendor_has_bid > 0 ? '#2e7d32' : '#1976d2',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap'
                    }}>
                      {tender.vendor_has_bid > 0 ? 'Bid Submitted' : 'Open for Bidding'}
                    </div>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '1rem',
                    marginBottom: '1rem',
                    fontSize: '0.9rem'
                  }}>
                    <div>
                      <span style={{ color: '#666' }}>Estimated Value:</span>
                      <br />
                      <span style={{ fontWeight: 'bold', color: '#238548' }}>
                        {formatCurrency(tender.estimated_value)}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: '#666' }}>Days Left:</span>
                      <br />
                      <span style={{
                        fontWeight: 'bold',
                        color: getDaysRemaining(tender.submission_deadline) <= 3 ? '#c62828' : '#f57c00'
                      }}>
                        {getDaysRemaining(tender.submission_deadline)} days
                      </span>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '0.85rem',
                    color: '#666'
                  }}>
                    <span>Deadline: {formatDate(tender.submission_deadline)}</span>
                    <span style={{ fontWeight: 'bold', color: '#238548' }}>
                      {tender.vendor_has_bid > 0 ? 'View Bid' : 'Place Bid'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Bid Modal */}
      {showBidModal && selectedTender && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.5rem',
                color: '#238548'
              }}>
                {selectedTender.vendor_has_bid > 0 ? 'View Bid' : 'Submit Bid'}
              </h3>
              <button
                onClick={() => setShowBidModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '2rem',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0.5rem',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f0f0f0'}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                ×
              </button>
            </div>

            {/* Tender Details */}
            <div style={{
              background: '#f8f9fa',
              padding: '1.5rem',
              borderRadius: '8px',
              marginBottom: '2rem'
            }}>
              <h4 style={{ margin: '0 0 1rem 0', color: '#238548' }}>
                {selectedTender.title}
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
                fontSize: '0.9rem'
              }}>
                <div>
                  <span style={{ color: '#666' }}>Tender Number:</span>
                  <br />
                  <span style={{ fontWeight: 'bold' }}>{selectedTender.tender_number}</span>
                </div>
                <div>
                  <span style={{ color: '#666' }}>Estimated Value:</span>
                  <br />
                  <span style={{ fontWeight: 'bold' }}>{formatCurrency(selectedTender.estimated_value)}</span>
                </div>
                <div>
                  <span style={{ color: '#666' }}>Deadline:</span>
                  <br />
                  <span style={{ fontWeight: 'bold' }}>{formatDate(selectedTender.submission_deadline)}</span>
                </div>
                <div>
                  <span style={{ color: '#666' }}>Days Left:</span>
                  <br />
                  <span style={{
                    fontWeight: 'bold',
                    color: getDaysRemaining(selectedTender.submission_deadline) <= 3 ? '#c62828' : '#f57c00'
                  }}>
                    {getDaysRemaining(selectedTender.submission_deadline)} days
                  </span>
                </div>
              </div>
            </div>

            {selectedTender.vendor_has_bid > 0 ? (
              /* View Existing Bid */
              <div style={{
                background: '#e8f5e9',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid #c8e6c9'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#2e7d32' }}>✓ Bid Already Submitted</h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '1rem',
                  fontSize: '0.9rem'
                }}>
                  <div>
                    <span style={{ color: '#666' }}>Quoted Amount:</span>
                    <br />
                    <span style={{ fontWeight: 'bold' }}>{formatCurrency(selectedTender.vendor_quoted_amount)}</span>
                  </div>
                  <div>
                    <span style={{ color: '#666' }}>Submission Date:</span>
                    <br />
                    <span style={{ fontWeight: 'bold' }}>{formatDate(selectedTender.vendor_bid_date)}</span>
                  </div>
                </div>
                <div style={{ marginTop: '1rem' }}>
                  <button
                    onClick={() => handleDownloadQuotation(selectedTender.vendor_bid_id)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#238548',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#1e7139'}
                    onMouseLeave={(e) => e.target.style.background = '#238548'}
                  >
                    📄 Download Quotation
                  </button>
                </div>
              </div>
            ) : (
              /* Submit New Bid Form */
              <form onSubmit={handleSubmitBid}>
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {/* Quoted Amount */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: 'bold',
                      color: '#333'
                    }}>
                      Quoted Amount (₹) *
                    </label>
                    <input
                      type="number"
                      value={bidData.quotedAmount}
                      onChange={(e) => setBidData(prev => ({ ...prev, quotedAmount: e.target.value }))}
                      placeholder="Enter your quoted amount"
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* Tax Percentage */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: 'bold',
                      color: '#333'
                    }}>
                      Tax Percentage (%)
                    </label>
                    <input
                      type="number"
                      value={bidData.taxPercentage}
                      onChange={(e) => setBidData(prev => ({ ...prev, taxPercentage: e.target.value }))}
                      placeholder="18"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* Calculated Fields */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '1rem'
                  }}>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: 'bold',
                        color: '#333'
                      }}>
                        Tax Amount (₹)
                      </label>
                      <input
                        type="text"
                        value={bidData.taxAmount}
                        readOnly
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e0e0e0',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          backgroundColor: '#f8f9fa'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: 'bold',
                        color: '#333'
                      }}>
                        Total Amount (₹)
                      </label>
                      <input
                        type="text"
                        value={bidData.totalAmount}
                        readOnly
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e0e0e0',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          backgroundColor: '#f8f9fa'
                        }}
                      />
                    </div>
                  </div>

                  {/* Payment Terms */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: 'bold',
                      color: '#333'
                    }}>
                      Payment Terms
                    </label>
                    <textarea
                      value={bidData.paymentTerms}
                      onChange={(e) => setBidData(prev => ({ ...prev, paymentTerms: e.target.value }))}
                      placeholder="Describe your payment terms..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  {/* Delivery Period */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: 'bold',
                      color: '#333'
                    }}>
                      Delivery Period
                    </label>
                    <input
                      type="text"
                      value={bidData.deliveryPeriod}
                      onChange={(e) => setBidData(prev => ({ ...prev, deliveryPeriod: e.target.value }))}
                      placeholder="e.g., 30 days from order"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* Warranty Period */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: 'bold',
                      color: '#333'
                    }}>
                      Warranty Period
                    </label>
                    <input
                      type="text"
                      value={bidData.warrantyPeriod}
                      onChange={(e) => setBidData(prev => ({ ...prev, warrantyPeriod: e.target.value }))}
                      placeholder="e.g., 1 year"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* Remarks */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: 'bold',
                      color: '#333'
                    }}>
                      Remarks
                    </label>
                    <textarea
                      value={bidData.remarks}
                      onChange={(e) => setBidData(prev => ({ ...prev, remarks: e.target.value }))}
                      placeholder="Any additional remarks..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  {/* File Upload */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: 'bold',
                      color: '#333'
                    }}>
                      Upload Quotation PDF *
                    </label>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                    <p style={{
                      margin: '0.5rem 0 0 0',
                      fontSize: '0.85rem',
                      color: '#666'
                    }}>
                      Only PDF files up to 10MB are allowed.
                    </p>
                  </div>

                  {/* Submit Button */}
                  <div style={{
                    display: 'flex',
                    gap: '1rem',
                    marginTop: '2rem'
                  }}>
                    <button
                      type="submit"
                      disabled={uploading}
                      style={{
                        flex: 1,
                        padding: '1rem',
                        background: uploading ? '#ccc' : '#238548',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        cursor: uploading ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!uploading) e.target.style.background = '#1e7139';
                      }}
                      onMouseLeave={(e) => {
                        if (!uploading) e.target.style.background = '#238548';
                      }}
                    >
                      {uploading ? 'Submitting...' : 'Submit Bid'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowBidModal(false)}
                      style={{
                        padding: '1rem 2rem',
                        background: '#6b7280',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#4b5563'}
                      onMouseLeave={(e) => e.target.style.background = '#6b7280'}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            )}

            {uploadSuccess && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                background: '#e8f5e9',
                border: '1px solid #c8e6c9',
                borderRadius: '8px',
                color: '#2e7d32',
                textAlign: 'center'
              }}>
                ✅ Bid submitted successfully! The modal will close automatically.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorLiveBidding;
